import { alfred } from '@api/api/alfred';
import { stripMentionMarkers } from '@design/chat/mention-composer/markers';
import { config } from '@terminal/config/app.config';
import { useActions } from '@terminal/stores/use-actions/useActions';
import { hasActions } from '@terminal/stores/use-actions/utils/hasActions';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { apiStream } from '@terminal/utils/api-stream/apiStream';
import { create } from '@terminal/utils/store/createStore';
import { useDocumentSourcesTableStore } from '@terminal/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';

import {
  DocumentResponse,
  DocumentSearchStore,
  MessageStatus,
} from './use-document-search.types';
import {
  appendReply,
  createNewMessage,
  updateMessage,
} from './utils/updateMessage';

export const useDocumentSearch = create<DocumentSearchStore>((set, get) => ({
  sessionId: crypto.randomUUID(),
  controller: new AbortController(),
  messages: [],
  documentContextMessages: [],
  addDocumentContextMessage: message => {
    set(state => ({
      documentContextMessages: [...state.documentContextMessages, message],
    }));
  },
  clearDocumentContextMessages: () => {
    set({ documentContextMessages: [] });
  },
  isMessagePending: false,
  abortRequest: () => {
    get().controller.abort();
    set({ isMessagePending: false });
  },
  addFeedback: ({ sessionId, requestId, feedback }) => {
    apiFetch(alfred.feedback.path, {
      method: 'post',
      body: {
        session_id: sessionId,
        request_id: requestId,
        feedback,
      },
    });
    set(state => ({
      ...state,
      messages: state.messages.map(m => {
        return {
          ...m,
          reply: m.reply.map(reply =>
            'request_id' in reply && reply.request_id === requestId
              ? { ...reply, feedback }
              : reply
          ),
        };
      }),
    }));
  },
  resetSession: hideResetCompletedMessage => {
    const sessionId = crypto.randomUUID();
    set(state => ({
      sessionId,
      messages: [
        ...state.messages,
        ...(hideResetCompletedMessage
          ? []
          : [createNewMessage({ status: 'complete', type: 'reset' })]),
      ],
    }));
  },
  append: message => {
    set(state => ({
      messages: [...state.messages, message],
    }));
  },
  setActionsFromReply: (primaryReply: DocumentResponse) => {
    if (hasActions(primaryReply?.data)) {
      useActions.getState().setActions(primaryReply.data.actions);
    }
  },
  removeReply: (messageId, source) => {
    set(({ messages }) => ({
      messages: messages.map(m => {
        if (m.id === messageId) {
          return { ...m, reply: m.reply.filter(r => r.source !== source) };
        }

        return m;
      }),
    }));
  },
  update: message => {
    set(state => ({
      messages: updateMessage({ messages: state.messages, message }),
    }));
  },
  sendMessage: async ({ question, ancillary, mentions }) => {
    useActions.getState().removeAllActions();
    const id = crypto.randomUUID();

    const documents = ancillary?.documents;

    set(state => ({
      ...state,
      controller: new AbortController(),
      messages: [
        ...state.messages,
        createNewMessage(
          {
            id,
            question,
          },
          documents
        ),
      ],
      isMessagePending: true,
    }));

    const onComplete = (
      status: MessageStatus,
      hasExceededUsageLimit?: boolean
    ) => {
      const existingMessage = get().messages.find(m => m.id === id);

      if (existingMessage) {
        get().update({
          ...existingMessage,
          reply: existingMessage.reply,
          status,
          hasExceededUsageLimit,
        });

        if (status === 'complete') {
          const primaryReply = existingMessage.reply[0];
          get().setActionsFromReply(primaryReply || {});

          const isContextEnabled = get().isSearchWithinContext;

          if (isContextEnabled) {
            const documentIds = primaryReply?.data?.document_ids;

            if (documentIds && documentIds.length) {
              useDocumentSourcesTableStore
                .getState()
                .setActiveDocumentIds(documentIds);
            }
          }
        }

        get().addDocumentContextMessage({
          ...existingMessage,
          reply: existingMessage.reply.filter(
            r => r.source === 'document-assistant'
          ),
        });
      }

      set(() => ({
        isMessagePending: false,
      }));
    };

    const onReadChunk = (reply: DocumentResponse[]) => {
      set(({ messages }) => ({
        messages: appendReply(messages, { id, reply }),
      }));
    };

    apiStream<DocumentResponse>({
      body: {
        question: stripMentionMarkers(question),
        session_id: get().sessionId,
        target: 'document-assistant',
        ancillary,
        ...(mentions?.length ? { mentions } : {}),
      },
      onReadChunk,
      onComplete: () => onComplete('complete'),
      onError: error => {
        const isUsageLimitExceeded = error?.status === 429;
        onComplete('error', isUsageLimitExceeded);
      },
      onAbort: () => onComplete('abort'),
      controller: get().controller,
      endpoint: `${config.api.root}${alfred.streaming.path}`,
    });
  },
  resetStore: () => {
    useActions.getState().removeAllActions();
    get().controller.abort();

    set(() => ({
      sessionId: crypto.randomUUID(),
      messages: [],
      isMessagePending: false,
      documentContextMessages: [],
      priorDocumentContextReplyMap: {},
    }));
    useDocumentSourcesTableStore.getState().setActiveDocumentIds([]);
  },

  shouldResetStateOnSubmit: false,
  setShouldResetStateOnSubmit: value => {
    set({ shouldResetStateOnSubmit: value });
  },

  isSearchWithinContext: false,
  setIsSearchWithinContext: value => {
    set({ isSearchWithinContext: value });
  },
  priorDocumentContextReplyMap: {},
  appendDocumentContextReply: (id, reply) => {
    set({
      priorDocumentContextReplyMap: {
        ...get().priorDocumentContextReplyMap,
        [id]: reply,
      },
    });
  },
}));
